import { createFileRoute } from '@tanstack/react-router'
import { useCurrentBatch } from '@/lib/hooks/useCurrentBatch'
import { useHome } from '@/lib/hooks/useHome'
import {
  ProgressActivityCard,
  ProgressHero,
  ProgressSkillsCard,
  ProgressStatsRow,
  UpcomingMilestonesCard,
  AreasToImproveCard,
} from '@/features/Progress'

export const Route = createFileRoute('/_student/progress/')({ component: ProgressPage })

function ProgressPage() {
  const { batchId, isAll } = useCurrentBatch()
  // useHome has real server-side aggregation across all enrolled batches via
  // the 'all' sentinel (same as the dashboard home and schedule pages) — no
  // need to fall back to an arbitrary single batch.
  const effectiveBatchId = isAll ? 'all' : (batchId ?? undefined)
  const { data: home, isLoading: homeLoading } = useHome(effectiveBatchId)
  // UpcomingMilestonesCard/AreasToImproveCard are genuinely single-batch
  // concepts with no aggregate form — hide them under "All Courses" rather
  // than silently showing only one arbitrarily-picked batch's data.
  const cardBatchId = isAll ? null : (batchId ?? null)

  if (homeLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 p-6">
        <div className="h-36 animate-pulse rounded-xl border border-[#e8e4df] bg-[#f2f0ed]" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border border-[#e8e4df] bg-[#f2f0ed]" />
          ))}
        </div>
      </div>
    )
  }

  if (!home) return null

  if (!home.readiness || !home.streak) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <p className="text-sm text-zinc-500">
          Progress data is being calculated. Check back after your first session.
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <ProgressHero readiness={home.readiness} streak={home.streak} stats={home.stats} />
      <ProgressStatsRow
        stats={home.stats}
        readiness={home.readiness}
      />
      {cardBatchId && <UpcomingMilestonesCard batchId={cardBatchId} />}
      {cardBatchId && <AreasToImproveCard batchId={cardBatchId} />}
      <div className="grid gap-4 lg:grid-cols-2">
        <ProgressSkillsCard skills={home.skills} />
        <ProgressActivityCard heatmap={home.heatmap} streak={home.streak} />
      </div>
    </div>
  )
}
